import React, { useState, useEffect } from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useProfile, useAuth } from "hooks";
import { colors, fontSize } from "style";
import { MEMBER_STATUS, VETTING_STEPS } from "constants/index";
import { formatDate } from "utils";
import { CandidateSubmitButton, CandidateApproveButton, CandidateRejectButton } from "components/Candidate";
import { Col, Row } from "components/Containers";
import { ProfileStatusUpdateButton } from "components/Profile";
import { Toggle } from "components/Form";
import { TOGGLE_SIZE } from "components/Form/Toggle";
import Tooltip from "components/Tooltip";
import { ProgressChecklist } from "components/Progress";

/**
 * CandidateProgress
 *
 * @param {String}     profileId
 * @param {Boolean}    isAdmin
 * @param {Boolean}    isActiveMember
 * @param {Boolean}    isArchived
 */
const CandidateProgress = ({ profileId, isAdmin, isActiveMember, isArchived, ...props }) => {
  const { profileId: authProfileId, isRecruiter } = useAuth();
  const showApproveRejectButtons = isAdmin && !isActiveMember && !isArchived;
  const showStatusUpdateButton = isAdmin && !isActiveMember && !isArchived;

  const {
    isProfileComplete,
    hasCompletedAnyChallenges,
    isInterviewComplete,
    vettingConfiguration,
    updateProfile,
    data: profileData,
  } = useProfile({ profileId });

  const updateVettingConfiguration = (step, value) => {
    const newVettingConfiguration = vettingConfiguration.map((config) => ({
      name: config.name,
      required: config.name === step ? value : config.required,
    }));

    updateProfile(profileId, { vettingConfiguration: newVettingConfiguration });
  };

  return (
    <Col gap="1rem">
      <ProgressChecklist
        title="Profile.CandidateProgress.Title"
        steps={vettingConfigurationData({
          vettingConfiguration,
          isAdmin,
          isActiveMember,
          isProfileComplete,
          hasCompletedAnyChallenges,
          isInterviewComplete,
          canUpdateVetting: isAdmin || isRecruiter,
          updateVettingConfiguration,
        })}
        {...props}
      />
      {authProfileId === profileId && <CandidateSubmitButton />}
      {showApproveRejectButtons && (
        <>
          <CandidateApproveButton profileId={profileId} />
          <CandidateRejectButton profileId={profileId} />
        </>
      )}
      {isAdmin && isActiveMember && (
        <div css={styles.approved}>
          <FormattedMessage
            id="Candidate.CandidateProgress.ApprovedDetails"
            values={{
              name: profileData?.profile?.approvedBy?.name,
              date: formatDate(profileData?.profile?.memberSince, "MMMM Do, YYYY"),
            }}
          />
        </div>
      )}
      {showStatusUpdateButton && <ProfileStatusUpdateButton profile={profileData.profile} />}
    </Col>
  );
};

const vettingStepCompleted = ({ step, isProfileComplete, hasCompletedAnyChallenges, isInterviewComplete }) => {
  switch (step) {
    case VETTING_STEPS.codeChallenge:
      return hasCompletedAnyChallenges;
    case VETTING_STEPS.videoInterview:
      return isInterviewComplete;
    case VETTING_STEPS.profileCompletion:
      return isProfileComplete;
    default:
      return false;
  }
};

const vettingConfigurationData = ({
  vettingConfiguration,
  isAdmin,
  isActiveMember,
  canUpdateVetting,
  updateVettingConfiguration,
  ...props
}) => {
  const [data, setData] = useState(vettingConfiguration);

  useEffect(() => {
    setData(vettingConfiguration);
  }, [vettingConfiguration]);

  const handleUpdate = (name, value, index) => {
    setData(data.map((item, i) => (i === index ? { ...item, required: value } : item)));
    updateVettingConfiguration(name, value);
  };

  return data?.map((config, index) => ({
    title: (
      <Row>
        <span css={styles.label(!canUpdateVetting || config.required)}>
          <FormattedMessage id={`Candidate.CandidateProgress.Step.${config.name}.Title${isAdmin ? `Admin` : ``}`} />
        </span>
        {!config.required && !canUpdateVetting && (
          <span css={styles.optional}>
            <FormattedMessage id={`Global.Optional`} />
          </span>
        )}
      </Row>
    ),
    isRequired: config.required,
    isComplete: vettingStepCompleted({ step: config.name, ...props }),
    details: canUpdateVetting && (
      <Tooltip
        tooltipId={`Candidate.CandidateProgress.ToggleTooltip${config.required ? `Disable` : `Enable`}${
          isActiveMember ? `ReadOnly` : ``
        }`}
        hideIcon
      >
        <Toggle
          name={config.name}
          onChange={(n, v) => handleUpdate(n, v, index)}
          value={config.required}
          size={TOGGLE_SIZE.small}
          timeOut={0}
          disabled={isActiveMember}
        />
      </Tooltip>
    ),
  }));
};

const useRender = (profileId) => {
  const { data, loading, error } = useProfile({ profileId });

  if (loading || error) return false;

  return ![MEMBER_STATUS.archived].includes(data?.profile?.status);
};

const styles = {
  label: (required) => css`
    font-weight: ${required ? `500` : `400`};
    color: ${required ? colors.purpleRainDark2 : colors.grayAnatomyLight1};
  `,
  approved: css`
    margin-top: 1rem;
    background: ${colors.lightGreen};
    color: ${colors.darkGreen};
    font-size: ${fontSize.xxsmall};
    text-align: center;
    padding: 1rem;
    border-radius: 0.6rem;
  `,
  optional: css`
    font-size: ${fontSize.xxsmall};
    color: ${colors.grayAnatomyLight1};
    margin-left: auto;
    font-weight: 400;
  `,
};

CandidateProgress.propTypes = {
  profileId: PropTypes.string,
  isAdmin: PropTypes.bool,
  isActiveMember: PropTypes.bool,
  isArchived: PropTypes.bool,
};

export { useRender };

export default CandidateProgress;
